/**
 * Modular Building Discovery Protection System
 * Prevents softlocking by guaranteeing critical building discovery
 *
 * HOW TO USE:
 * 1. Add/remove protections in DISCOVERY_PROTECTIONS array
 * 2. Call getForcedBiome() in moveToNode before normal biome selection
 * 3. If it returns a biome, use that instead of random selection
 *
 * BIOME GUARANTEES:
 * Ensures minimum biome counts are met by a certain tile threshold.
 * Configure in BIOME_GUARANTEES array below.
 */

import { BIOMES } from './biomes';

// ============================================================================
// PROTECTION RULES - Edit this to add/remove protections
// ============================================================================

export const DISCOVERY_PROTECTIONS = [
  // Protection 1: Critical Tier 1 Buildings (Basic Economy)
  {
    id: 'tier1-economy',
    name: 'Tier 1 Economy Buildings',
    enabled: true,
    criticalBuildings: ['woodcutter', 'forager', 'stonePicker', 'gathererCamp'],
    checkAtTile: 15, // Check if discovered by tile 15
    guaranteeByTile: 25, // Force spawn by tile 25 if missing
    priority: 1, // Higher = more important
    description: 'Ensures basic resource production buildings are discoverable'
  },

  // Protection 2: Food Production
  {
    id: 'food-production',
    name: 'Food Buildings',
    enabled: true,
    criticalBuildings: ['forager', 'hunter'],
    checkAtTile: 10,
    guaranteeByTile: 20,
    priority: 2,
    description: 'Ensures at least one food-producing building'
  },

  // Protection 3: Knowledge Production (disabled - Overgrown Shrine handles this)
  {
    id: 'knowledge-production',
    name: 'Knowledge Buildings',
    enabled: false, // Disabled - Overgrown Shrine force-spawn handles this
    criticalBuildings: ['library'],
    checkAtTile: 7,
    guaranteeByTile: 7,
    priority: 3,
    description: 'Ensures knowledge production (handled by Overgrown Shrine)'
  },

  // Protection 4: Housing/Population
  {
    id: 'housing',
    name: 'Housing Buildings',
    enabled: true,
    criticalBuildings: ['homestead'],
    checkAtTile: 12,
    guaranteeByTile: 20,
    priority: 4,
    description: 'Ensures housing is discoverable'
  },
];

// ============================================================================
// BIOME GUARANTEES - Ensures minimum biome counts by tile threshold
// ============================================================================

export const BIOME_GUARANTEES = [
  {
    id: 'core-biomes',
    name: 'Core Biome Diversity',
    enabled: true,
    priority: 10, // Higher than building protections
    startCheckingAtTile: 5, // Start checking early (was: guaranteeByTile - 10)
    guaranteeByTile: 20, // Must have all minimums by tile 20 (was: 30)
    biomes: {
      mountains: 3,
      plains: 3,
      forest: 3,
    },
    description: 'Ensures at least 3 mountains, 3 plains, and 3 forests by tile 20'
  },
];

// ============================================================================
// BIOME MAPPING - Which biomes can spawn which buildings
// ============================================================================

/**
 * Get all biomes that can provide a specific building
 * @param {string} buildingId - Building ID to search for
 * @returns {Array} Array of biome objects that have this building
 */
export function getBiomesWithBuilding(buildingId) {
  return Object.values(BIOMES).filter(biome =>
    biome.associatedBuildings?.includes(buildingId)
  );
}

/**
 * Get the best biome to spawn for a missing building
 * Prioritizes biomes with lower minAge that are currently spawnable
 * @param {string} buildingId - Building to find biome for
 * @param {number} currentAge - Player's current age/era
 * @returns {Object|null} Biome object or null if none found
 */
export function getBestBiomeForBuilding(buildingId, currentAge) {
  const validBiomes = getBiomesWithBuilding(buildingId)
    .filter(biome => biome.minAge <= currentAge) // Must be spawnable at current age
    .filter(biome => !biome.isDungeonBiome) // Don't force dungeon biomes
    .sort((a, b) => a.minAge - b.minAge); // Prefer lower tier biomes

  return validBiomes.length > 0 ? validBiomes[0] : null;
}

// ============================================================================
// PROTECTION CHECKER - Returns what needs to be protected
// ============================================================================

/**
 * Check which protections need to trigger based on current progress
 * @param {number} tilesExplored - Total tiles discovered
 * @param {Array<string>} discoveredBuildings - Buildings already discovered
 * @param {number} currentAge - Player's current age/era
 * @returns {Array} Array of protection actions needed
 */
export function checkProtections(tilesExplored, discoveredBuildings, currentAge) {
  const actions = [];

  for (const protection of DISCOVERY_PROTECTIONS) {
    if (!protection.enabled) continue;

    // Check if we've reached the check threshold
    if (tilesExplored >= protection.checkAtTile) {
      // Find which critical buildings are still missing
      const missingBuildings = protection.criticalBuildings.filter(
        buildingId => !discoveredBuildings.includes(buildingId)
      );

      if (missingBuildings.length > 0) {
        // Should we force spawn now?
        const shouldForceSpawn = tilesExplored >= protection.guaranteeByTile;

        if (shouldForceSpawn) {
          // Find biomes that can provide the missing buildings
          const biomeOptions = [];
          for (const buildingId of missingBuildings) {
            const biome = getBestBiomeForBuilding(buildingId, currentAge);
            if (biome) {
              biomeOptions.push({ buildingId, biome });
            }
          }

          if (biomeOptions.length > 0) {
            actions.push({
              protectionId: protection.id,
              priority: protection.priority,
              missingBuildings,
              biomeOptions,
              action: 'FORCE_SPAWN',
              message: `Protection "${protection.name}" triggered - forcing biome spawn`
            });
          }
        } else {
          // Just a warning - not forcing yet
          actions.push({
            protectionId: protection.id,
            priority: protection.priority,
            missingBuildings,
            action: 'WARNING',
            tilesUntilForce: protection.guaranteeByTile - tilesExplored,
            message: `Warning: Missing ${missingBuildings.join(', ')} - will force spawn in ${protection.guaranteeByTile - tilesExplored} tiles`
          });
        }
      }
    }
  }

  // Sort by priority (higher priority first)
  return actions.sort((a, b) => b.priority - a.priority);
}

// ============================================================================
// FORCE SPAWN HANDLER - Modifies biome selection
// ============================================================================

/**
 * Apply protection by forcing a specific biome to spawn
 * Call this BEFORE normal biome selection logic
 * @param {number} tilesExplored - Total tiles discovered
 * @param {Array<string>} discoveredBuildings - Buildings already discovered
 * @param {number} currentAge - Player's current age/era
 * @returns {Object|null} Forced biome or null if no protection needed
 */
export function getForcedBiome(tilesExplored, discoveredBuildings, currentAge) {
  const actions = checkProtections(tilesExplored, discoveredBuildings, currentAge);

  // Get highest priority FORCE_SPAWN action
  const forceAction = actions.find(action => action.action === 'FORCE_SPAWN');

  if (forceAction && forceAction.biomeOptions.length > 0) {
    // Return the best biome option (first one, since we sorted by minAge)
    const forced = forceAction.biomeOptions[0];
    console.log(`🛡️ Building Protection: Forcing ${forced.biome.name} (${forced.biome.id}) to provide ${forced.buildingId}`);
    return forced.biome;
  }

  return null;
}

// ============================================================================
// HELPER - Get protection status for debugging/UI
// ============================================================================

/**
 * Get human-readable status of all protections
 * Useful for debugging or showing in dev console
 * @param {number} tilesExplored - Total tiles discovered
 * @param {Array<string>} discoveredBuildings - Buildings already discovered
 * @param {number} currentAge - Player's current age/era
 * @returns {Array} Status of each protection
 */
export function getProtectionStatus(tilesExplored, discoveredBuildings, currentAge) {
  return DISCOVERY_PROTECTIONS.map(protection => {
    const missingBuildings = protection.criticalBuildings.filter(
      buildingId => !discoveredBuildings.includes(buildingId)
    );

    return {
      id: protection.id,
      name: protection.name,
      enabled: protection.enabled,
      status: missingBuildings.length === 0 ? 'SATISFIED' :
              tilesExplored >= protection.guaranteeByTile ? 'TRIGGERED' :
              tilesExplored >= protection.checkAtTile ? 'WARNING' : 'OK',
      missingBuildings,
      tilesUntilCheck: Math.max(0, protection.checkAtTile - tilesExplored),
      tilesUntilForce: Math.max(0, protection.guaranteeByTile - tilesExplored)
    };
  });
}

// ============================================================================
// BIOME GUARANTEE CHECKER - Ensures minimum biome counts
// ============================================================================

/**
 * Count biomes in discovered nodes
 * @param {Object} discoveredNodes - Map of discovered node data
 * @returns {Object} Count of each biome type
 */
export function countDiscoveredBiomes(discoveredNodes) {
  const counts = {};
  Object.values(discoveredNodes).forEach(node => {
    if (node.biome && !node.isHub) { // Exclude kingdom (hub)
      counts[node.biome] = (counts[node.biome] || 0) + 1;
    }
  });
  return counts;
}

/**
 * Check which biomes need to be force-spawned based on guarantees
 * @param {number} tilesExplored - Total tiles discovered
 * @param {Object} discoveredNodes - Map of discovered node data
 * @param {number} currentAge - Player's current age/era
 * @returns {Object|null} Biome to force spawn, or null if no guarantee needed
 */
export function checkBiomeGuarantees(tilesExplored, discoveredNodes, currentAge) {
  const biomeCounts = countDiscoveredBiomes(discoveredNodes);

  for (const guarantee of BIOME_GUARANTEES) {
    if (!guarantee.enabled) continue;

    // Only check if we're approaching the guarantee threshold
    // Use explicit startCheckingAtTile if defined, otherwise fallback to 10 tiles before deadline
    const checkStartTile = guarantee.startCheckingAtTile ?? (guarantee.guaranteeByTile - 10);
    if (tilesExplored < checkStartTile) continue;

    // Find which biomes are below their minimum count
    const missingBiomes = [];
    for (const [biomeId, minCount] of Object.entries(guarantee.biomes)) {
      const currentCount = biomeCounts[biomeId] || 0;
      const deficit = minCount - currentCount;

      if (deficit > 0) {
        // Calculate urgency: how many tiles left vs how many biomes we still need
        const tilesRemaining = guarantee.guaranteeByTile - tilesExplored;
        missingBiomes.push({
          biomeId,
          currentCount,
          minCount,
          deficit,
          urgency: deficit / Math.max(1, tilesRemaining), // Higher = more urgent
        });
      }
    }

    // If we have missing biomes, return the most urgent one
    if (missingBiomes.length > 0) {
      // Sort by urgency (highest first), then by deficit (highest first)
      missingBiomes.sort((a, b) => {
        if (b.urgency !== a.urgency) return b.urgency - a.urgency;
        return b.deficit - a.deficit;
      });

      const mostUrgent = missingBiomes[0];
      const biome = BIOMES[mostUrgent.biomeId];

      // Only force if biome is available at current age
      if (biome && biome.minAge <= currentAge) {
        return {
          biome,
          reason: `Biome guarantee: Need ${mostUrgent.deficit} more ${biome.name} (${mostUrgent.currentCount}/${mostUrgent.minCount}) by tile ${guarantee.guaranteeByTile}`,
          currentCount: mostUrgent.currentCount,
          minCount: mostUrgent.minCount,
          tilesRemaining: guarantee.guaranteeByTile - tilesExplored,
        };
      }
    }
  }

  return null;
}

/**
 * Get forced biome based on biome guarantees
 * Call this AFTER dungeon pity timer but BEFORE beacon/building protections
 * @param {number} tilesExplored - Total tiles discovered
 * @param {Object} discoveredNodes - Map of discovered node data
 * @param {number} currentAge - Player's current age/era
 * @returns {Object|null} Forced biome object or null
 */
export function getForcedBiomeByGuarantee(tilesExplored, discoveredNodes, currentAge) {
  const result = checkBiomeGuarantees(tilesExplored, discoveredNodes, currentAge);

  if (result) {
    console.log(`🌍 Biome Guarantee: Forcing ${result.biome.name} (${result.biome.id}) - ${result.reason}`);
    return result.biome;
  }

  return null;
}

/**
 * Get biome guarantee status for debugging/UI
 * @param {number} tilesExplored - Total tiles discovered
 * @param {Object} discoveredNodes - Map of discovered node data
 * @returns {Array} Status of each biome guarantee
 */
export function getBiomeGuaranteeStatus(tilesExplored, discoveredNodes) {
  const biomeCounts = countDiscoveredBiomes(discoveredNodes);

  return BIOME_GUARANTEES.map(guarantee => {
    const biomeStatus = {};
    let allSatisfied = true;

    for (const [biomeId, minCount] of Object.entries(guarantee.biomes)) {
      const currentCount = biomeCounts[biomeId] || 0;
      const satisfied = currentCount >= minCount;
      if (!satisfied) allSatisfied = false;

      biomeStatus[biomeId] = {
        current: currentCount,
        required: minCount,
        satisfied,
      };
    }

    return {
      id: guarantee.id,
      name: guarantee.name,
      enabled: guarantee.enabled,
      status: allSatisfied ? 'SATISFIED' :
              tilesExplored >= guarantee.guaranteeByTile ? 'TRIGGERED' : 'ACTIVE',
      biomes: biomeStatus,
      tilesUntilDeadline: Math.max(0, guarantee.guaranteeByTile - tilesExplored),
    };
  });
}

export default {
  DISCOVERY_PROTECTIONS,
  BIOME_GUARANTEES,
  getBiomesWithBuilding,
  getBestBiomeForBuilding,
  checkProtections,
  getForcedBiome,
  getProtectionStatus,
  countDiscoveredBiomes,
  checkBiomeGuarantees,
  getForcedBiomeByGuarantee,
  getBiomeGuaranteeStatus,
};
